/**
 * OneQrew Button Component
 *
 * Vollständig nach Brand Guide implementiert mit allen Varianten:
 * - MainPurple (Primary Container - Purple)
 * - MainYellow (Primary Container Alt - Yellow)
 * - Secondary (Outlined/Ghost Button)
 * - Tertiary (Text/Link Button)
 *
 * Alle Buttons unterstützen States: Default, Hover, Pressed, Disabled, Focus
 */

import React from 'react';
import { Button as MuiButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { borderRadius, interactionStates } from '../../theme/tokens';

/**
 * MainPurple Button (Primary Container - Purple Background)
 */
export const MainPurpleButton = styled(MuiButton)(({ theme }) => ({
  backgroundColor: theme.palette.oneqrew.primaryContainer,
  color: theme.palette.oneqrew.onPrimaryContainer,
  borderRadius: borderRadius.xxs, // 8px für Buttons
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 500,
  textTransform: 'none',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',

  '&:hover': {
    backgroundColor: theme.palette.oneqrew.primaryContainer,
    backgroundImage: `linear-gradient(${interactionStates.stateLayer.hovered}, ${interactionStates.stateLayer.hovered})`,
    boxShadow: theme.shadows[1],
  },

  '&:active': {
    backgroundImage: `linear-gradient(${interactionStates.stateLayer.pressed}, ${interactionStates.stateLayer.pressed})`,
  },

  '&:focus-visible': {
    outline: `${interactionStates.focusRingWidth} solid ${interactionStates.focussed}`,
    outlineOffset: interactionStates.focusRingOffset,
  },

  '&.Mui-disabled': {
    backgroundColor: theme.palette.oneqrew.disabledContainer,
    color: theme.palette.oneqrew.onDisabledContainer,
  },

  // Size Variants
  '&.MuiButton-sizeSmall': {
    padding: '8px 16px',
    fontSize: '14px',
    borderRadius: borderRadius.xxs,
  },

  '&.MuiButton-sizeLarge': {
    padding: '16px 32px',
    fontSize: '18px',
    borderRadius: borderRadius.xxs, // Auch Large Buttons: 8px
  },
}));

/**
 * MainYellow Button (Primary Container Alt - Yellow Background)
 */
export const MainYellowButton = styled(MuiButton)(({ theme }) => ({
  backgroundColor: theme.palette.oneqrew.primaryContainerAlt,
  color: theme.palette.oneqrew.onPrimaryContainerAlt,
  borderRadius: borderRadius.xxs, // 8px für Buttons
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 500,
  textTransform: 'none',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',

  '&:hover': {
    backgroundColor: theme.palette.oneqrew.primaryContainerAlt,
    backgroundImage: `linear-gradient(${interactionStates.stateLayer.hovered}, ${interactionStates.stateLayer.hovered})`,
    boxShadow: theme.shadows[1],
  },

  '&:active': {
    backgroundImage: `linear-gradient(${interactionStates.stateLayer.pressed}, ${interactionStates.stateLayer.pressed})`,
  },

  '&:focus-visible': {
    outline: `${interactionStates.focusRingWidth} solid ${interactionStates.focussed}`,
    outlineOffset: interactionStates.focusRingOffset,
  },

  '&.Mui-disabled': {
    backgroundColor: theme.palette.oneqrew.disabledContainer,
    color: theme.palette.oneqrew.onDisabledContainer,
  },

  '&.MuiButton-sizeSmall': {
    padding: '16px 24px',
    fontSize: '14px',
    borderRadius: borderRadius.xxs,
  },

  '&.MuiButton-sizeLarge': {
    padding: '32px 40px',
    fontSize: '18px',
    borderRadius: borderRadius.m,
  },
}));

/**
 * Secondary Button (Outlined/Ghost - White Background)
 */
export const SecondaryButton = styled(MuiButton)(({ theme }) => ({
  backgroundColor: theme.palette.oneqrew.secondaryContainer,
  color: theme.palette.oneqrew.onSecondaryContainer,
  border: `2px solid ${theme.palette.oneqrew.onSecondaryContainer}`,
  borderRadius: borderRadius.xxs, // 8px für Buttons
  padding: '10px 22px', // Adjusted for border
  fontSize: '16px',
  fontWeight: 500,
  textTransform: 'none',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',

  '&:hover': {
    backgroundColor: theme.palette.oneqrew.secondaryContainer,
    backgroundImage: `linear-gradient(${interactionStates.stateLayer.hovered}, ${interactionStates.stateLayer.hovered})`,
    border: `2px solid ${theme.palette.oneqrew.onSecondaryContainer}`,
  },

  '&:active': {
    backgroundImage: `linear-gradient(${interactionStates.stateLayer.pressed}, ${interactionStates.stateLayer.pressed})`,
  },

  '&:focus-visible': {
    outline: `${interactionStates.focusRingWidth} solid ${interactionStates.focussed}`,
    outlineOffset: interactionStates.focusRingOffset,
  },

  '&.Mui-disabled': {
    backgroundColor: theme.palette.oneqrew.disabledContainer,
    color: theme.palette.oneqrew.onDisabledContainer,
    borderColor: theme.palette.oneqrew.onDisabledContainer,
  },

  '&.MuiButton-sizeSmall': {
    padding: '6px 14px',
    fontSize: '14px',
    borderRadius: borderRadius.xxs,
  },

  '&.MuiButton-sizeLarge': {
    padding: '14px 30px',
    fontSize: '18px',
    borderRadius: borderRadius.xxs, // Auch Large Buttons: 8px
  },
}));

/**
 * Tertiary Button (Text/Link Button - No Background)
 */
export const TertiaryButton = styled(MuiButton)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: theme.palette.oneqrew.onTertiaryContainer,
  borderRadius: borderRadius.xxs, // 8px
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 500,
  textTransform: 'none',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',

  '&:hover': {
    backgroundColor: interactionStates.stateLayer.hovered,
  },

  '&:active': {
    backgroundColor: interactionStates.stateLayer.pressed,
  },

  '&:focus-visible': {
    outline: `${interactionStates.focusRingWidth} solid ${interactionStates.focussed}`,
    outlineOffset: interactionStates.focusRingOffset,
  },

  '&.Mui-disabled': {
    color: theme.palette.oneqrew.onDisabledContainer,
  },

  '&.MuiButton-sizeSmall': {
    padding: '8px 16px',
    fontSize: '14px',
  },

  '&.MuiButton-sizeLarge': {
    padding: '16px 32px',
    fontSize: '18px',
  },
}));

/**
 * Unified OneQrewButton Component
 *
 * Usage:
 * <OneQrewButton variant="mainPurple">Click me</OneQrewButton>
 * <OneQrewButton variant="mainYellow">Click me</OneQrewButton>
 * <OneQrewButton variant="secondary">Click me</OneQrewButton>
 * <OneQrewButton variant="tertiary">Click me</OneQrewButton>
 */
export const OneQrewButton = React.forwardRef(
  ({ variant = 'mainPurple', ...props }, ref) => {
    switch (variant) {
      case 'mainYellow':
        return <MainYellowButton ref={ref} {...props} />;
      case 'secondary':
        return <SecondaryButton ref={ref} {...props} />;
      case 'tertiary':
        return <TertiaryButton ref={ref} {...props} />;
      case 'mainPurple':
      default:
        return <MainPurpleButton ref={ref} {...props} />;
    }
  }
);

OneQrewButton.displayName = 'OneQrewButton';

// Export einzelner Komponenten für direkten Import
export default OneQrewButton;
